import React, { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { AppShell } from "../components/AppShell.js";
import { EmptyState, ErrorNote, Spinner, StatusPill, timecode } from "../components/ui.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { api, type ProjectSummary } from "../api.js";
import { useJobActivity, useOnJobSettled, type ActiveJob } from "../lib/jobActivity.js";
import { useEta } from "../lib/useEta.js";
import { TrashIcon } from "../components/icons.js";
import { etaLabel, formatUsd } from "@veyso/core";
import { capture } from "../lib/posthog.js";
import { needsWelcome } from "./Welcome.js";

/**
 * Minutes and seconds, without the hundredths the editor needs.
 *
 * `timecode` is built for a scrubber, where a frame is a hundredth of a second
 * and the digits are being read against a playhead. On a card it is a label,
 * and "0:45.33" spends three characters saying something nobody asked.
 */
const clock = (seconds: number): string => {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  return `${Math.floor(safe / 60)}:${String(Math.round(safe % 60)).padStart(2, "0")}`;
};

/**
 * How long ago, in the width a card column has.
 *
 * "3 days ago" under a 70px heading truncates to "3 days a…", which is worse
 * than saying less: the label above it already says Updated, so the number and
 * its unit carry the whole meaning between them.
 */
const compactAgo = (iso: string): string => {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  return days < 7 ? `${days}d` : `${Math.round(days / 7)}w`;
};

/**
 * One of the three numbers under a card.
 *
 * The dash is drawn rather than left blank: an empty cell reads as a rendering
 * fault, and "no spend yet" is a fact worth showing.
 */
const Figure: React.FC<{ label: string; value: string | null; quiet?: boolean }> = ({ label, value, quiet }) => (
  <div className="flex min-w-0 flex-col gap-px">
    <span className="text-[10px] text-ink-400">{label}</span>
    <span className={`tnum truncate text-[12.5px] ${quiet ? "text-ink-400" : "font-semibold text-ink-200"}`}>
      {value ?? "—"}
    </span>
  </div>
);

/**
 * The dashboard answers one question: which project needs me next? Cards are
 * ordered by recency and lead with the frame, because a person recognises their
 * own recording faster than they read its name — which is also why the frame
 * takes the position a progress ring would in a project tracker. Veyso has no
 * percentage to put there: a project is a draft, then processing, then ready,
 * then complete, and a ring reading 100% on everything finished is decoration
 * wearing the clothes of data.
 */
/**
 * What a project card opens.
 *
 * Three destinations, not two. A finished project goes to the editor and an
 * unfinished one to its overview — but a launch video has no timeline behind
 * it, so the editor would open onto nothing. Its card is the file.
 *
 * `download` rather than a route: there is no page to show. The video is the
 * whole of what was made, and a screen whose only content is a download button
 * is a screen nobody asked for.
 */
export type CardDestination = { kind: "route"; to: string };

/**
 * Where a card goes, as a decision rather than as markup.
 *
 * Pulled out because the rule it encodes is the one that makes the whole
 * feature safe to ship, and a rule living inside JSX is a rule nothing can
 * test. A launch video is `completed` like any other project, so the plain
 * two-way branch would send the first finished one straight to the editor,
 * onto a timeline that does not exist.
 *
 * A finished launch video used to download on click. That was wrong twice
 * over: it handed somebody a file they had never seen, and it left nowhere to
 * say which of ten looks it was made in or to ask for a different one. It goes
 * to its own page now, and the download is a button there.
 */
export function cardDestination(project: Pick<ProjectSummary, "id" | "kind" | "status">): CardDestination {
  const finished = project.status === "ready" || project.status === "completed";
  if (finished && project.kind !== "launch") return { kind: "route", to: `/projects/${project.id}/editor` };
  return { kind: "route", to: `/projects/${project.id}` };
}

/**
 * The card's spend: what the customer was charged, as the statement shows it.
 *
 * "Free" for a video covered by a free allowance, so a card never shows $0.00
 * beside a statement line that says the video was free. Nothing until the
 * project has been charged.
 */
export function spendLabel(project: Pick<ProjectSummary, "chargedUsd" | "freeVideo">): string | null {
  if (project.chargedUsd === null) return null;
  if (project.chargedUsd > 0) return formatUsd(project.chargedUsd);
  return project.freeVideo ? "Free" : formatUsd(0);
}

/** What sits where a card's frame goes. */
export type CardPreview =
  | { kind: "image"; src: string }
  | { kind: "video"; src: string }
  | { kind: "creating" }
  | { kind: "failed" }
  | { kind: "no-frame" };

/**
 * The card's picture, decided apart from drawing it.
 *
 * A launch video finished before thumbnails were taken has none, and the old
 * rule — no thumbnail and not failed means still being made — called it
 * "Creating intro video" for ever. A finished launch video has its file, so
 * that is what it shows instead.
 */
export function cardPreview(
  project: Pick<ProjectSummary, "kind" | "status" | "thumbnailUrl" | "videoUrl">,
): CardPreview {
  if (project.thumbnailUrl) return { kind: "image", src: project.thumbnailUrl };
  if (project.kind !== "launch") return { kind: "no-frame" };
  if (project.videoUrl) return { kind: "video", src: project.videoUrl };
  return project.status === "failed" ? { kind: "failed" } : { kind: "creating" };
}

/**
 * A launch card's time left, smoothed under its job's id like the jobs menu's
 * row for the same job, so the two read the same. A component of its own so
 * the hook is not called inside the card list's loop.
 */
const LaunchTimeLeft: React.FC<{ job: ActiveJob | null }> = ({ job }) => {
  const shown = useEta(job?.id ?? "launch-none", job?.eta);
  return <span>{etaLabel(shown) ?? "Usually about 10 minutes"}</span>;
};

const CardShell: React.FC<{ project: ProjectSummary; children: React.ReactNode }> = ({ project, children }) => (
  <Link to={cardDestination(project).to} className="flex min-w-0 flex-1 flex-col">
    {children}
  </Link>
);

export const Dashboard: React.FC = () => {
  /**
   * A fresh account is sent to set its brand kit before it sees this.
   *
   * Decided here rather than in the router, because the answer needs the
   * settings and the router has no way to wait for them. Undecided while it
   * loads, so the dashboard is not painted and then replaced — which would show
   * somebody an empty project list for a moment and call it their workspace.
   *
   * Only this route. Somebody following a link to a specific project has a
   * reason to be there, and a setup step in front of it would be an obstacle
   * rather than an introduction.
   */
  const [firstRun, setFirstRun] = useState<boolean | null>(null);
  // The running jobs, for the time left on a launch video still being made.
  const { active } = useJobActivity();

  useEffect(() => {
    void api
      .settings()
      .then((current) => setFirstRun(needsWelcome(current.brandKit)))
      // An unreadable settings call is not a reason to hold somebody out of
      // their own dashboard; the kit is editable from settings either way.
      .catch(() => setFirstRun(false));
  }, []);

  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ProjectSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await api.deleteProject(pendingDelete.id);
      capture("project_deleted", { project_id: pendingDelete.id, deletion_surface: "dashboard" });
      // Drop it locally rather than waiting for the next poll, so the row
      // disappears the moment the dialog closes.
      setProjects((current) => current?.filter((p) => p.id !== pendingDelete.id) ?? null);
      setPendingDelete(null);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  const loadRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const data = await api.listProjects();
        if (!cancelled) {
          setProjects(data);
          setError(null);
        }
      } catch (cause) {
        if (!cancelled) setError((cause as Error).message);
      }
    };
    // Shared with the settle hook below, which needs the same loader but lives
    // outside this effect's closure.
    loadRef.current = load;

    void load();
    // Anything mid-pipeline changes on its own, so the list refreshes itself.
    // The five-second poll keeps the list roughly fresh; `useOnJobSettled`
    // below is what makes a finished job show up immediately rather than up to
    // five seconds later.
    const timer = setInterval(() => void load(), 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  // A finished job changes a row's status and whether it has an export, so
  // the list is refetched the moment one settles rather than up to five
  // seconds later.
  useOnJobSettled(() => void loadRef.current?.());

  const anyLive = projects?.some((project) => project.status === "processing" || project.status === "rendering");

  // No New project action in the header. It only ever appeared here — the
  // header renders whatever the page passes it, and no other page passes this —
  // so it was a second primary button on the one screen that already has the
  // tile, and absent from every screen where it would have been the only way
  // in.
  //
  // What it costs: scrolled down a long list, starting a project means
  // scrolling back to the top. The tile is the first thing in the grid, so that
  // is one gesture, and the mark in the header returns here from anywhere else.
  // Before anything else is drawn, and before the empty state in particular.
  if (firstRun === null) return null;
  if (firstRun) return <Navigate to="/welcome" replace />;

  return (
    <AppShell live={anyLive}>
      <div className="mb-7 flex items-end justify-between">
        <div>
          <h1 className="text-[26px] font-semibold text-ink-100">Projects</h1>
          <p className="mt-1 text-[13px] text-ink-300">Recordings turned into finished videos.</p>
        </div>
        {projects ? <span className="tnum text-[12px] text-ink-400">{projects.length} total</span> : null}
      </div>

      {error ? <ErrorNote>{error}</ErrorNote> : null}

      {!projects && !error ? (
        <div className="flex items-center gap-2 py-10 text-[13px] text-ink-300">
          <Spinner /> Loading projects…
        </div>
      ) : null}

      {projects?.length === 0 ? (
        <EmptyState
          title="No projects yet"
          body="Upload a screen recording and the pipeline will transcribe it, write narration, find the moments that matter, and hand you a first draft to edit."
          action={
            <Link to="/projects/new" className="btn btn-primary mt-1">
              Create your first project
            </Link>
          }
        />
      ) : null}

      {projects && projects.length > 0 ? (
        /* Cards rather than rows, because the thing being listed is a video and
           the frame is what identifies it. `auto-fill` rather than a fixed
           column count: the sidebar and the window both move, and a grid that
           reflows needs no breakpoints to argue about. */
        <div className="grid grid-cols-[repeat(auto-fill,minmax(232px,1fr))] gap-3.5">
          {/* First, and it stays first. For someone with one project this is
              half the page, and for someone with forty it is the thing they
              came back to do. */}
          <Link
            to="/projects/new"
            className="group/new flex min-h-[216px] flex-col items-center justify-center gap-2 rounded-[10px] border border-dashed border-[var(--edge-strong)] bg-bay-900 text-ink-300 transition-colors hover:border-ember-500 hover:bg-ember-500/[0.05] hover:text-ember-600"
          >
            <span aria-hidden className="text-[26px] leading-none">
              +
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.1em]">New project</span>
          </Link>

          {/* The card is a link, and delete is a sibling laid over it. Nesting a
              button inside an anchor is invalid markup and gives the two
              overlapping click targets. */}
          {projects.map((project) => (
            <div
              key={project.id}
              className="surface group relative flex flex-col overflow-hidden transition-colors hover:border-[var(--edge-strong)]"
            >
              <CardShell project={project}>
                <div className="relative aspect-video shrink-0 overflow-hidden border-b border-[var(--edge)] bg-bay-800">
                  {(() => {
                    const preview = cardPreview(project);
                    if (preview.kind === "image") {
                      return <img src={preview.src} alt="" className="h-full w-full object-cover" />;
                    }
                    if (preview.kind === "video") {
                      // `#t=` asks for a frame a little way in; the opening of
                      // a launch film is an animation starting from nothing.
                      return (
                        <video
                          src={`${preview.src}#t=3`}
                          muted
                          playsInline
                          preload="metadata"
                          className="pointer-events-none h-full w-full object-cover"
                        />
                      );
                    }
                    /*
                      A launch video says what is happening to it, because
                      nobody asked for it and "no frame yet" on a card that
                      appeared by itself explains nothing. It takes several
                      minutes, so the wait is named, with the time left when the
                      jobs feed has it, rather than left to be discovered.
                    */
                    return (
                      <div className="flex h-full w-full flex-col items-center justify-center gap-1 px-3 text-center text-[11px] text-ink-400">
                        {preview.kind === "creating" ? (
                          <>
                            <span className="font-medium text-ink-200">Creating intro video</span>
                            <LaunchTimeLeft
                              job={active.find((job) => job.projectId === project.id && job.type === "launch") ?? null}
                            />
                          </>
                        ) : preview.kind === "failed" ? (
                          <span>could not be made</span>
                        ) : (
                          <span>no frame yet</span>
                        )}
                      </div>
                    );
                  })()}

                  {/* On the frame rather than beside it, because that is where
                      every video player puts it and because it costs the card
                      no vertical space. Its own dark chip: the frame underneath
                      is arbitrary, so nothing from the palette can be trusted
                      to read against it. */}
                  {project.durationSeconds ? (
                    <span className="tnum absolute bottom-1.5 right-1.5 rounded-[4px] bg-black/70 px-1.5 py-px text-[10.5px] font-medium text-white">
                      {clock(project.durationSeconds)}
                    </span>
                  ) : null}
                </div>

                <div className="flex min-w-0 flex-1 flex-col gap-2 p-3">
                  {/* One line with the full name on hover. A screen recorder
                      writes the date and time into a filename, and a card that
                      grew to fit one would break the row it sits in. */}
                  <p className="truncate text-[13.5px] font-semibold text-ink-100" title={project.name}>
                    {project.name}
                  </p>

                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    {/*
                      What kind of video this is, before anything about its
                      state. The two behave differently when clicked — one
                      opens an editor, one opens a preview with a download —
                      and a list that does not say which is a list where the
                      same click does two things.

                      Launch is the one that gets the colour, because it is the
                      rarer and the newer of the two; a product video is what
                      almost every card already is, and marking the majority is
                      noise.

                      The colour is in the border and the tint, not the text.
                      Ember on its own 10% tint measures 2.75:1 — nowhere near
                      readable at 10.5px — and the brand orange is too light to
                      carry small type on white at all. Ink keeps the label
                      legible and the orange still marks the card.
                    */}
                    <span
                      className={`rounded-full border px-1.5 py-px text-[10.5px] font-medium ${
                        project.kind === "launch"
                          ? "border-ember-500/45 bg-ember-500/10 text-ink-100"
                          : "border-[var(--edge)] bg-bay-800 text-ink-300"
                      }`}
                    >
                      {project.kind === "launch" ? "Launch" : "Product"}
                    </span>
                    <StatusPill status={project.status} />
                    {/* Not a status: having a finished video is a different
                        fact from being finished editing, and a project can be
                        both exported and still open. */}
                    {project.hasOutput ? (
                      <span className="rounded-full border border-[var(--edge)] bg-bay-800 px-1.5 py-px text-[10.5px] font-medium text-ink-300">
                        Exported
                      </span>
                    ) : null}
                  </div>

                  {/* The same three figures the row showed, so nothing is lost
                      in the move. Pushed to the bottom so they line up across
                      cards whose names wrap differently. */}
                  <div className="mt-auto grid grid-cols-3 gap-1 border-t border-[var(--edge)] pt-2.5">
                    <Figure label="Length" value={project.durationSeconds ? clock(project.durationSeconds) : null} />
                    <Figure label="Spend" value={spendLabel(project)} />
                    <Figure label="Updated" value={compactAgo(project.updatedAt)} quiet />
                  </div>
                </div>
              </CardShell>

              <button
                type="button"
                className="absolute right-1.5 top-1.5 rounded-[5px] bg-black/55 p-1.5 text-white opacity-0 transition-all hover:bg-signal-bad focus-visible:opacity-100 group-hover:opacity-100"
                title={`Delete ${project.name}`}
                aria-label={`Delete ${project.name}`}
                onClick={() => setPendingDelete(project)}
              >
                <TrashIcon />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={`Delete "${pendingDelete?.name}"?`}
        body={
          <>
            This removes the recording, transcript, script, timeline and any rendered video. It cannot be undone.
            {pendingDelete?.durationSeconds ? (
              <span className="mt-2 block text-ink-400">
                {timecode(pendingDelete.durationSeconds)} of footage will be deleted from disk.
              </span>
            ) : null}
          </>
        }
        confirmLabel="Delete project"
        destructive
        busy={deleting}
        onCancel={() => (deleting ? undefined : setPendingDelete(null))}
        onConfirm={() => void confirmDelete()}
      />
    </AppShell>
  );
};
